'use client';

import React from 'react';
import styled from 'styled-components';

const RadioContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
`;

const HiddenRadio = styled.input.attrs({ type: 'radio' })`
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
`;

const StyledRadio = styled.div<{ $checked: boolean; $disabled?: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${props => 
    props.$checked ? props.theme.colors.accent : props.theme.colors.gray[300]};
  background: ${props => props.$checked ? props.theme.colors.white : props.theme.colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
  opacity: ${props => props.$disabled ? 0.5 : 1};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};

  &:hover {
    ${props => !props.$disabled && `
      border-color: ${props.theme.colors.accent};
    `}
  }

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${props => props.theme.colors.accent};
    transform: scale(${props => props.$checked ? 1 : 0});
    transition: transform 0.15s ease;
  }
`;

const Label = styled.label<{ $disabled?: boolean }>`
  font-size: 14px;
  color: ${props => props.$disabled ? props.theme.colors.gray[400] : props.theme.colors.gray[700]};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  line-height: 1.5;
`;

export interface RadioProps {
  name: string;
  value: string;
  label: string;
  checked: boolean;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  labelStyle?: React.CSSProperties;
}

/**
 * Radio Component
 * 
 * A styled radio button input component
 * 
 * @component
 * @example
 * ```tsx
 * <Radio
 *   name="option"
 *   value="yes"
 *   label="Yes"
 *   checked={selected === 'yes'}
 *   onChange={(value) => setSelected(value)}
 * />
 * ```
 */
export const Radio: React.FC<RadioProps> = ({
  name,
  value,
  label,
  checked,
  onChange,
  disabled = false,
  className,
  labelStyle
}) => {
  const handleChange = () => {
    if (!disabled) {
      onChange(value);
    }
  };

  return (
    <RadioContainer onClick={handleChange} className={className}>
      <HiddenRadio
        name={name}
        value={value}
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        aria-checked={checked}
      />
      <StyledRadio $checked={checked} $disabled={disabled} />
      <Label $disabled={disabled} style={labelStyle}>{label}</Label>
    </RadioContainer>
  );
};

export default Radio;

